import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  Check,
  CheckCheck,
  ArrowLeft,
  Loader2,
  X,
  Pencil,
  Trash2,
  Pin,
  PinOff,
  Phone,
  Users,
} from 'lucide-react';
import Avatar from '../Avatar';
import ConfirmModal from '../ConfirmModal';
import VoiceNotePlayer from '../VoiceNotePlayer';
import SpotifyCard from './SpotifyCard';
import { useAuth } from '../../contexts/AuthContext';
import RowActionsMenu from '../ui/RowActionsMenu';
import MessageInput from './MessageInput';
import MediaPreviewModal from './MediaPreviewModal';
import NowPlaying from '../spotify/NowPlaying';
import { parseApiDate, formatPKTShortTime, formatPKTTime12h } from '../../utils/time';

function karachiDateKey(date) {
  return date.toLocaleDateString('en-CA', { timeZone: 'Asia/Karachi' });
}

function formatLastSeen(lastSeen) {
  if (!lastSeen) return null;
  const date = parseApiDate(lastSeen);
  if (!date) return null;
  const thenKey = karachiDateKey(date);
  const todayKey = karachiDateKey(new Date());
  if (thenKey === todayKey) {
    return `last seen today at ${formatPKTTime12h(lastSeen)}`;
  }
  const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000);
  if (thenKey === karachiDateKey(yesterday)) {
    return `last seen yesterday at ${formatPKTTime12h(lastSeen)}`;
  }
  return `last seen ${formatDistanceToNow(date, { addSuffix: true })}`;
}

function renderContentWithMentions(content, mentions = [], onMentionClick) {
  if (!content) return null;
  const parts = [];
  const regex = /@(user|task|project):([^\s@]+(?:\s+[^\s@]+)*)/g;
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = regex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push(<span key={key++}>{content.slice(lastIndex, match.index)}</span>);
    }
    const type = match[1];
    const display = match[2];
    const mention = mentions.find(
      (m) => m.type === type && (m.display === display || m.id)
    );
    const label =
      (typeof mention?.display === 'string' && mention.display) || display;
    parts.push(
      <button
        key={key++}
        type="button"
        onClick={() => mention && onMentionClick?.(mention)}
        className="inline-flex items-center px-1.5 py-0.5 mx-0.5 rounded-md bg-primary-50 text-primary-700 text-xs font-medium hover:bg-primary-100 dark:bg-primary-900/30 dark:text-primary-300 dark:hover:bg-primary-900/50"
      >
        @{label}
      </button>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < content.length) {
    parts.push(<span key={key++}>{content.slice(lastIndex)}</span>);
  }
  return parts;
}

const ChatWindow = ({
  conversation,
  messages = [],
  loading = false,
  loadingOlder = false,
  hasMoreOlder = false,
  onLoadOlder,
  typingUser = null,
  isOnline = false,
  lastSeen = null,
  onBack,
  onSend,
  onTyping,
  onUploadImage,
  sending = false,
  messagingEnabled = true,
  pendingRequest = null,
  onAcceptRequest,
  onDeclineRequest,
  acceptingRequest = false,
  decliningRequest = false,
  onEditMessage,
  onDeleteMessage,
  onDeleteConversation,
  onPinConversation,
  onPinMessage,
  onLeaveGroup,
  onStartCall,
  deletingConversation = false,
  deletingMessage = false,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [editingMessage, setEditingMessage] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const bottomRef = useRef(null);
  const listRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const pendingScrollRestoreRef = useRef(null);
  const prevConversationIdRef = useRef(null);
  const prevMessageCountRef = useRef(0);

  useEffect(() => {
    setEditingMessage(null);
  }, [conversation?.id]);

  const isPending =
    !!pendingRequest ||
    conversation?.kind === 'pending_request' ||
    conversation?.kind === 'group_invite';
  const isGroupInvite = conversation?.kind === 'group_invite';
  const hasLeft = !!conversation?.has_left;
  const requestDirection =
    pendingRequest?.direction || conversation?.requestDirection || null;
  const requestMessage =
    pendingRequest?.message || conversation?.request?.message || '';
  const isGroup = conversation?.type === 'group' || isGroupInvite;
  const other = conversation?.other_user;
  const pendingInvites = conversation?.pending_invites || [];
  const pinnedMessages = hasLeft ? [] : conversation?.pinned_messages || [];
  const displayName = isGroup
    ? conversation?.name || conversation?.request?.group_name || 'Group'
    : other?.full_name || other?.username || 'Chat';
  const groupDescription = (
    conversation?.description ||
    conversation?.request?.group_description ||
    ''
  ).trim();
  const memberCount =
    conversation?.member_count || conversation?.participant_ids?.length || 0;
  const statusText = isPending
    ? requestDirection === 'incoming'
      ? isGroupInvite
        ? 'Group invite · Accept to join'
        : 'Chat request · Accept to message'
      : 'Chat request · Waiting for approval'
    : hasLeft
      ? 'You left this group'
      : typingUser
        ? `${typingUser} is typing…`
        : isGroup
          ? pendingInvites.length > 0
            ? `${memberCount} members · ${pendingInvites.length} invite${
                pendingInvites.length === 1 ? '' : 's'
              } pending`
            : groupDescription
              ? `${memberCount} members · ${groupDescription}`
              : `${memberCount} members`
          : isOnline
            ? 'online'
            : formatLastSeen(
                lastSeen || other?.last_seen || other?.chat_last_seen || other?.last_login
              );

  const sortedMessages = useMemo(
    () =>
      [...messages].sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at)
      ),
    [messages]
  );

  // Reset scroll behavior when switching conversations
  useEffect(() => {
    if (conversation?.id !== prevConversationIdRef.current) {
      prevConversationIdRef.current = conversation?.id || null;
      stickToBottomRef.current = true;
      pendingScrollRestoreRef.current = null;
      prevMessageCountRef.current = 0;
    }
  }, [conversation?.id]);

  // Preserve viewport when older messages are prepended
  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el || pendingScrollRestoreRef.current == null) return;
    const previousHeight = pendingScrollRestoreRef.current;
    el.scrollTop = el.scrollHeight - previousHeight;
    pendingScrollRestoreRef.current = null;
  }, [sortedMessages.length, loadingOlder]);

  // Stick to bottom for new messages / typing, not when loading history
  useEffect(() => {
    const count = sortedMessages.length;
    const grew = count > prevMessageCountRef.current;
    prevMessageCountRef.current = count;

    if (!grew && !typingUser) return;
    if (pendingScrollRestoreRef.current != null) return;
    if (!stickToBottomRef.current) return;

    if (typeof bottomRef.current?.scrollIntoView === 'function') {
      bottomRef.current.scrollIntoView({
        behavior: grew && count > 1 ? 'smooth' : 'auto',
      });
    }
  }, [sortedMessages.length, typingUser, conversation?.id]);

  const handleScroll = () => {
    const el = listRef.current;
    if (!el) return;

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distanceFromBottom < 96;

    if (
      el.scrollTop <= 72 &&
      hasMoreOlder &&
      !loadingOlder &&
      typeof onLoadOlder === 'function'
    ) {
      pendingScrollRestoreRef.current = el.scrollHeight;
      onLoadOlder();
    }
  };

  const handleMentionClick = (mention) => {
    if (!mention?.id) return;
    if (mention.type === 'task') navigate(`/tasks/${mention.id}`);
    if (mention.type === 'project') navigate(`/projects/${mention.id}`);
    if (mention.type === 'user') navigate(`/team`);
  };

  const handleComposerSend = async (payload) => {
    if (payload?.editingMessageId) {
      await onEditMessage?.(payload);
      setEditingMessage(null);
      return;
    }
    await onSend?.(payload);
  };

  const handleDeleteMessage = (msg) => {
    setConfirmState({
      type: 'message',
      message: msg,
      title: 'Delete message',
      body: 'Are you sure you want to delete this message? This cannot be undone.',
      confirmText: 'Delete',
    });
  };

  const handleDeleteConversation = () => {
    const isGroupConv = conversation?.type === 'group';
    setConfirmState({
      type: 'conversation',
      title: isGroupConv ? 'Delete group' : 'Delete conversation',
      body: isGroupConv
        ? 'Permanently delete this group and all its messages? This cannot be undone.'
        : 'Remove this conversation from your list? You can still chat again if either of you sends a new message.',
      confirmText: 'Delete',
    });
  };

  const handleLeaveGroupClick = () => {
    setConfirmState({
      type: 'leave',
      title: 'Leave group',
      body: 'Leave this group? You will need a new invite to rejoin.',
      confirmText: 'Leave',
    });
  };

  const closeConfirm = () => {
    if (deletingConversation || deletingMessage) return;
    setConfirmState(null);
  };

  const handleConfirmAction = async () => {
    if (!confirmState) return;
    if (confirmState.type === 'message') {
      const msg = confirmState.message;
      await onDeleteMessage?.(msg);
      if (editingMessage?.id === msg?.id) setEditingMessage(null);
    } else if (confirmState.type === 'conversation') {
      await onDeleteConversation?.(conversation);
    } else if (confirmState.type === 'leave') {
      await onLeaveGroup?.(conversation);
    }
    setConfirmState(null);
  };

  if (!conversation) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-secondary-500 dark:text-secondary-400 p-6">
        Select a conversation to start chatting
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white dark:bg-secondary-900" data-testid="chat-window">
      <div className="flex items-center gap-3 px-3 py-3 border-b border-secondary-200 dark:border-secondary-700">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="lg:hidden p-2 min-h-[44px] min-w-[44px] rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5 text-secondary-600 dark:text-secondary-300" />
          </button>
        )}
        {isGroup ? (
          <div className="h-9 w-9 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 flex items-center justify-center shrink-0">
            <Users className="h-4 w-4" />
          </div>
        ) : (
          <Avatar
            blobName={other?.avatar}
            alt={other?.full_name || other?.username || 'User'}
            size="sm"
            className="shrink-0"
          />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-100 truncate inline-flex items-center gap-1">
            {conversation?.is_pinned && (
              <Pin className="h-3.5 w-3.5 text-primary-600 dark:text-primary-400" />
            )}
            {displayName}
          </p>
          {statusText && (
            <p
              className={`text-xs truncate ${
                typingUser || (!isGroup && isOnline)
                  ? 'text-primary-600 dark:text-primary-400'
                  : 'text-secondary-500 dark:text-secondary-400'
              }`}
              data-testid="chat-presence-status"
            >
              {statusText}
            </p>
          )}
          {!isGroup && !isPending && other?.id && (
            <NowPlaying userId={other.id} className="mt-0.5" />
          )}
        </div>
        {!isPending && (
          <div className="flex items-center gap-0.5">
            {!hasLeft && !isGroup && typeof onStartCall === 'function' && (
              <button
                type="button"
                onClick={() => onStartCall('voice')}
                className="p-2.5 min-h-[44px] min-w-[44px] rounded-lg text-secondary-500 hover:bg-secondary-100 dark:hover:bg-secondary-800"
                aria-label="Voice call"
                data-testid="start-voice-call"
                title="Voice call"
              >
                <Phone className="h-4 w-4" />
              </button>
            )}
            {!hasLeft && onPinConversation && (
              <button
                type="button"
                onClick={() => onPinConversation?.(conversation)}
                className="p-2.5 min-h-[44px] min-w-[44px] rounded-lg text-secondary-500 hover:bg-secondary-100 dark:hover:bg-secondary-800"
                aria-label={conversation?.is_pinned ? 'Unpin chat' : 'Pin chat'}
                data-testid="pin-conversation"
                title={conversation?.is_pinned ? 'Unpin chat' : 'Pin chat'}
              >
                {conversation?.is_pinned ? (
                  <PinOff className="h-4 w-4 text-primary-600" />
                ) : (
                  <Pin className="h-4 w-4" />
                )}
              </button>
            )}
            {isGroup && !hasLeft && onLeaveGroup && (
              <button
                type="button"
                onClick={handleLeaveGroupClick}
                className="p-2.5 min-h-[44px] min-w-[44px] rounded-lg text-secondary-500 hover:bg-amber-50 hover:text-amber-700 dark:hover:bg-amber-900/20"
                aria-label="Leave group"
                data-testid="leave-group"
                title="Leave group"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            {onDeleteConversation && (!isGroup || hasLeft) && (
              <button
                type="button"
                onClick={handleDeleteConversation}
                disabled={deletingConversation}
                className="p-2.5 min-h-[44px] min-w-[44px] rounded-lg text-secondary-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400 disabled:opacity-50"
                aria-label={isGroup ? 'Delete group' : 'Delete conversation'}
                data-testid="delete-conversation"
                title={isGroup ? 'Delete group' : 'Delete conversation'}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>

      {pinnedMessages.length > 0 && !isPending && (
        <div
          className="px-3 py-2 border-b border-secondary-200 dark:border-secondary-700 bg-primary-50/60 dark:bg-primary-900/20"
          data-testid="pinned-messages-bar"
        >
          {pinnedMessages.map((pin) => (
            <button
              key={pin.message_id}
              type="button"
              onClick={() => {
                const el = document.querySelector(
                  `[data-testid="chat-message-${pin.message_id}"]`
                );
                el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }}
              className="w-full flex items-start gap-2 text-left py-1"
            >
              <Pin className="h-3.5 w-3.5 text-primary-600 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-[11px] font-medium text-primary-700 dark:text-primary-300">
                  Pinned message
                  {pin.sender_name ? ` · ${pin.sender_name}` : ''}
                </p>
                <p className="text-xs text-secondary-700 dark:text-secondary-200 truncate">
                  {pin.content_preview || 'Message'}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}

      <div
        ref={listRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto overflow-x-hidden px-8 py-4 space-y-3"
        data-testid="chat-message-list"
      >
        {isPending && (
          <div
            className="mx-auto w-full max-w-sm rounded-2xl border border-secondary-200 bg-secondary-50 dark:border-secondary-700 dark:bg-secondary-800/80 px-5 py-6 text-center shadow-sm"
            data-testid="pending-request-panel"
          >
            {isGroupInvite ? (
              <div className="mx-auto h-16 w-16 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 flex items-center justify-center">
                <Users className="h-7 w-7" />
              </div>
            ) : (
              <div className="flex justify-center">
                <Avatar
                  blobName={other?.avatar}
                  alt={other?.full_name || other?.username || 'User'}
                  size="lg"
                  className="shrink-0"
                />
              </div>
            )}
            <p className="mt-4 text-base font-semibold text-secondary-900 dark:text-secondary-100">
              {isGroupInvite
                ? requestDirection === 'incoming'
                  ? `${other?.full_name || 'Someone'} invited you`
                  : `Waiting for ${other?.full_name || 'them'}`
                : requestDirection === 'incoming'
                  ? `${other?.full_name || 'Someone'} wants to chat`
                  : `Waiting for ${other?.full_name || 'them'} to accept`}
            </p>
            <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
              {isGroupInvite
                ? displayName
                : requestDirection === 'incoming'
                  ? 'Accept to start messaging'
                  : 'They’ll be able to message once they accept'}
            </p>
            {isGroupInvite && groupDescription ? (
              <p
                className="mt-3 text-sm text-secondary-600 dark:text-secondary-300"
                data-testid="group-invite-description"
              >
                {groupDescription}
              </p>
            ) : null}
            {!isGroupInvite && requestMessage ? (
              <p className="mt-3 text-sm text-secondary-600 dark:text-secondary-300 italic">
                “{requestMessage}”
              </p>
            ) : null}
            {requestDirection === 'incoming' && (
              <div className="mt-5 flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => onAcceptRequest?.(pendingRequest || conversation?.request)}
                  disabled={acceptingRequest || decliningRequest}
                  className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 min-h-[44px] rounded-full bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                >
                  <Check className="h-4 w-4" />
                  Accept
                </button>
                <button
                  type="button"
                  onClick={() => onDeclineRequest?.(pendingRequest || conversation?.request)}
                  disabled={acceptingRequest || decliningRequest}
                  className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 min-h-[44px] rounded-full bg-secondary-200/80 text-secondary-700 text-sm font-medium hover:bg-secondary-200 dark:bg-secondary-700 dark:text-secondary-100 dark:hover:bg-secondary-600 disabled:opacity-50"
                >
                  <X className="h-4 w-4" />
                  Decline
                </button>
              </div>
            )}
          </div>
        )}

        {!isPending &&
          !hasLeft &&
          isGroup &&
          (pendingInvites.length > 0 ||
            conversation?.created_by === user?.id) && (
          <div
            className="mx-auto w-full max-w-sm rounded-2xl border border-secondary-200 bg-secondary-50 dark:border-secondary-700 dark:bg-secondary-800/80 px-5 py-6 text-center shadow-sm"
            data-testid="group-created-card"
          >
            <div className="mx-auto h-16 w-16 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 flex items-center justify-center">
              <Users className="h-7 w-7" />
            </div>
            <p className="mt-4 text-base font-semibold text-secondary-900 dark:text-secondary-100">
              {conversation?.created_by === user?.id
                ? 'You created this group'
                : displayName}
            </p>
            <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
              Group · {memberCount} member{memberCount === 1 ? '' : 's'}
              {pendingInvites.length > 0
                ? ` · ${pendingInvites.length} invited`
                : ''}
            </p>
            {groupDescription ? (
              <p
                className="mt-3 text-sm text-secondary-600 dark:text-secondary-300"
                data-testid="group-description"
              >
                {groupDescription}
              </p>
            ) : null}
            {pendingInvites.length > 0 && (
              <div
                className="mt-4 text-left rounded-xl bg-white/80 dark:bg-secondary-900/50 border border-secondary-200/80 dark:border-secondary-700 px-3 py-2.5"
                data-testid="pending-group-invites-banner"
              >
                <p className="text-[11px] font-medium uppercase tracking-wide text-secondary-400 mb-1.5">
                  Invited
                </p>
                <p className="text-sm text-secondary-700 dark:text-secondary-200 leading-relaxed">
                  {pendingInvites
                    .map((inv) => inv.to_user_name || 'Member')
                    .join(', ')}
                </p>
              </div>
            )}
          </div>
        )}

        {hasLeft && !isPending && (
          <div
            className="mx-auto w-full max-w-sm rounded-2xl border border-secondary-200 bg-secondary-50 dark:border-secondary-700 dark:bg-secondary-800/80 px-5 py-6 text-center shadow-sm"
            data-testid="left-group-panel"
          >
            <div className="mx-auto h-16 w-16 rounded-full bg-secondary-200 dark:bg-secondary-700 text-secondary-600 dark:text-secondary-300 flex items-center justify-center">
              <Users className="h-7 w-7" />
            </div>
            <p className="mt-4 text-base font-semibold text-secondary-900 dark:text-secondary-100">
              You left {displayName}
            </p>
            <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
              As the creator, you can permanently delete this group.
            </p>
          </div>
        )}

        {loading && !isPending && sortedMessages.length === 0 && (
          <div className="flex items-center justify-center gap-2 text-sm text-secondary-500 py-8">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading messages…
          </div>
        )}

        {!isPending && (loadingOlder || hasMoreOlder) && sortedMessages.length > 0 && (
          <div
            className="flex items-center justify-center gap-2 text-xs text-secondary-500 dark:text-secondary-400 py-2"
            data-testid="chat-load-older"
          >
            {loadingOlder ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading older messages…
              </>
            ) : (
              <button
                type="button"
                onClick={() => {
                  const el = listRef.current;
                  if (el) pendingScrollRestoreRef.current = el.scrollHeight;
                  onLoadOlder?.();
                }}
                className="px-2 py-1 rounded-md hover:bg-secondary-100 dark:hover:bg-secondary-800"
              >
                Load older messages
              </button>
            )}
          </div>
        )}

        {!isPending && !loading && !hasMoreOlder && sortedMessages.length > 0 && (
          <div className="text-center text-[11px] text-secondary-400 dark:text-secondary-500 py-1">
            Beginning of conversation
          </div>
        )}

        {!isPending && sortedMessages.map((msg) => {
          if (msg.type === 'system') {
            return (
              <div
                key={msg.id}
                className="text-center text-xs text-secondary-400 dark:text-secondary-500 py-1"
              >
                {msg.content}
              </div>
            );
          }

          const isOwn = msg.sender_id === user?.id;
          const readByOthers =
            isOwn &&
            (msg.read_by || []).some((id) => id !== user?.id);

          const isMsgPinned = pinnedMessages.some((p) => p.message_id === msg.id);
          const canManage =
            !msg.is_deleted &&
            messagingEnabled &&
            !isPending &&
            (typeof onPinMessage === 'function' ||
              (isOwn &&
                (typeof onEditMessage === 'function' ||
                  typeof onDeleteMessage === 'function')));

          return (
            <div
              key={msg.id}
              className={`group flex ${isOwn ? 'justify-end' : 'justify-start'} animate-fade-in`}
              data-testid={`chat-message-${msg.id}`}
            >
              {/* Actions are absolutely positioned so Edit/⋯ never shifts bubble alignment */}
              <div className="relative max-w-[85%]">
                {canManage && (
                  <div
                    className={`absolute top-0.5 z-10 opacity-0 group-hover:opacity-100 transition-opacity ${
                      isOwn ? 'right-full mr-0.5' : 'left-full ml-0.5'
                    }`}
                  >
                    <RowActionsMenu
                      items={[
                        {
                          label: isMsgPinned ? 'Unpin' : 'Pin',
                          icon: isMsgPinned ? PinOff : Pin,
                          hidden: typeof onPinMessage !== 'function',
                          onClick: () => onPinMessage?.(msg, !isMsgPinned),
                        },
                        {
                          label: 'Edit',
                          icon: Pencil,
                          hidden:
                            !isOwn ||
                            typeof onEditMessage !== 'function' ||
                            msg.type === 'image' ||
                            msg.type === 'voice' ||
                            msg.type === 'spotify',
                          onClick: () => setEditingMessage(msg),
                        },
                        {
                          label: 'Delete',
                          icon: Trash2,
                          danger: true,
                          hidden: !isOwn || typeof onDeleteMessage !== 'function',
                          onClick: () => handleDeleteMessage(msg),
                        },
                      ]}
                    />
                  </div>
                )}
                <div
                  className={`rounded-2xl px-3 py-2 shadow-sm ${
                    isOwn
                      ? 'bg-primary-600 text-white rounded-br-md'
                      : 'bg-secondary-100 text-secondary-900 dark:bg-secondary-700 dark:text-secondary-100 rounded-bl-md'
                  } ${editingMessage?.id === msg.id ? 'ring-2 ring-primary-300 dark:ring-primary-500' : ''} ${
                    msg.type === 'voice' ? 'min-w-[220px]' : ''
                  } ${
                    msg.pending || String(msg.id || '').startsWith('temp-')
                      ? 'opacity-80'
                      : ''
                  }`}
                >
                  {msg.is_deleted ? (
                    <p className="text-sm italic opacity-70">Message deleted</p>
                  ) : (
                    <>
                      {msg.type === 'spotify' ? (
                        <SpotifyCard metadata={msg.metadata} isOwn={isOwn} />
                      ) : msg.type === 'voice' && msg.image_url ? (
                        <div
                          className={`rounded-lg ${
                            isOwn
                              ? 'bg-white/95 text-secondary-900'
                              : 'bg-white dark:bg-secondary-800'
                          }`}
                          data-testid="chat-voice-note"
                        >
                          <VoiceNotePlayer audioUrl={msg.image_url} />
                        </div>
                      ) : (
                        <>
                          {msg.image_url && (
                            <button
                              type="button"
                              onClick={() => setMediaPreview(msg.image_url)}
                              className="block w-full text-left rounded-lg overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 mb-1"
                              aria-label="Open image preview"
                              data-testid="chat-media-thumb"
                            >
                              <img
                                src={msg.image_url}
                                alt="Shared"
                                loading="lazy"
                                className="rounded-lg max-h-56 w-full object-cover cursor-zoom-in hover:opacity-95 transition-opacity"
                              />
                            </button>
                          )}
                          {msg.content && (
                            <div className="text-sm whitespace-pre-wrap break-words leading-relaxed">
                              {renderContentWithMentions(
                                msg.content,
                                msg.mentions,
                                handleMentionClick
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                  <div
                    className={`flex items-center gap-1 mt-1 ${
                      isOwn ? 'justify-end text-primary-100' : 'text-secondary-400'
                    }`}
                  >
                    <span className="text-[10px]">
                      {msg.created_at
                        ? formatPKTShortTime(msg.created_at)
                        : ''}
                      {msg.edited_at ? ' · edited' : ''}
                    </span>
                    {isOwn &&
                      (readByOthers ? (
                        <CheckCheck className="h-3 w-3" />
                      ) : (
                        <Check className="h-3 w-3" />
                      ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {messagingEnabled && !isPending && !hasLeft ? (
        <MessageInput
          onSend={handleComposerSend}
          onTyping={onTyping}
          onUploadImage={onUploadImage}
          sending={sending}
          conversationId={conversation?.id}
          conversationType={conversation?.type || 'direct'}
          editingMessage={editingMessage}
          onCancelEdit={() => setEditingMessage(null)}
        />
      ) : (
        <div
          className="border-t border-secondary-200 dark:border-secondary-700 px-4 py-3 text-center text-xs text-secondary-500 dark:text-secondary-400 bg-secondary-50 dark:bg-secondary-800/50"
          data-testid="messaging-locked"
        >
          {hasLeft
            ? 'Messaging unavailable after leaving'
            : isGroupInvite
              ? requestDirection === 'incoming'
                ? 'Accept this invite to join the group'
                : 'They can message once they accept your invite'
              : requestDirection === 'incoming'
                ? 'Accept this chat request to start messaging'
                : 'You can message once they accept your request'}
        </div>
      )}

      <ConfirmModal
        open={!!confirmState}
        title={confirmState?.title || 'Confirm'}
        message={confirmState?.body || 'Are you sure?'}
        onConfirm={handleConfirmAction}
        onCancel={closeConfirm}
        confirmText={confirmState?.confirmText || 'Delete'}
        cancelText="Cancel"
        confirmButtonStyle={confirmState?.type === 'leave' ? 'primary' : 'danger'}
        isLoading={
          confirmState?.type === 'conversation'
            ? deletingConversation
            : confirmState?.type === 'leave'
              ? false
              : deletingMessage
        }
      />

      <MediaPreviewModal
        open={Boolean(mediaPreview)}
        src={mediaPreview}
        onClose={() => setMediaPreview(null)}
      />
    </div>
  );
};

export default ChatWindow;
